import type { HealthStatus } from '@ctcells/contract'
import { API_BASE_URL, platform, REQUEST_TIMEOUT_MS } from '@ctcells/core'

/**
 * `GET /readyz` — **서버가 일을 받을 준비가 됐는가**(무인증 · 루트 경로 · `/api/v1` 아래가 아니다).
 *
 * 실물(2026-09-29): 정상 `{"status":"ok","checks":{"db":"ok","mqtt":"ok","scheduler":"ok","audit":"ok"}}`.
 * 🔴 **HTTP 상태로 판정하지 않는다 — 본문의 `checks` 를 읽는다.** 서버는 필수(`db`)만 503 으로 올리고, 부가 구성요소가
 *    실패하면 `degraded` + **200** 이다(`ctcells-core` `app/services/observability.judge_readiness`). 2026-09-29 재부팅 뒤
 *    스케줄러가 안 떠(`checks.scheduler: "not_started"`) 배치가 `running` 인 채 스텝이 안 나갔는데 그때도 200 이었다.
 * 🔴 **200 과 503 둘 다 본문을 읽는다**(계약 `readyz` — 두 응답 모두 `HealthStatus`).
 * 🔴 **못 읽은 것을 통과로 접지 않는다.** dev 서버에 프록시가 없으면 SPA `index.html` 이 200 으로 온다(실측) —
 *    JSON 이 아니거나 `checks` 가 없으면 `unreadable` 이다.
 *
 * ⚠ `orchestrator` 클라이언트를 쓰지 않는다 — 그쪽은 `API_BASE_URL`(`/api/v1`) 아래로만 나가고 토큰을 싣는다.
 *   이 경로는 루트이고 `security: []` 다. 전송은 여전히 플랫폼 포트(`platform.http`)를 거친다.
 */

/** 읽은 준비 상태 — `checks` 는 서버 원값 그대로(`"ok"` · `"not_started"` · `"stale (3601s > 30s)"` …) */
export interface ReadinessRead {
  kind: 'read'
  status: HealthStatus['status']
  checks: Readonly<Record<string, string>>
  /** 200 · 503 */
  httpStatus: number
}

/**
 * 못 읽었다 — `reason` 은 **사람 문구가 아니라 진단 원문**이다(`HTTP 404` · `not json` · `no checks` · 전송 오류 이름).
 * 화면 문구는 부르는 쪽이 사전(`ko.ts`)으로 감싼다.
 */
export interface ReadinessUnreadable {
  kind: 'unreadable'
  reason: string
}

export type Readiness = ReadinessRead | ReadinessUnreadable

/** 서버가 준비 판정을 싣는 HTTP 상태 — 이 둘 밖(404 · 502 …)은 본문이 `HealthStatus` 가 아니다 */
const READABLE_HTTP: ReadonlySet<number> = new Set([200, 503])
/** 계약 `HealthStatus.status` enum */
const STATUSES: ReadonlySet<string> = new Set(['ok', 'degraded', 'unavailable'])

/**
 * 루트 경로의 기준 — `API_BASE_URL` 이 절대 주소면 그 오리진, 상대면 같은 오리진(`''`).
 * 🔴 주소를 박지 않는다 — dev 는 Vite 프록시(`/readyz`), 배포는 같은 오리진 프록시가 받는다.
 */
function rootUrl(): string {
  return /^https?:\/\//i.test(API_BASE_URL) ? new URL(API_BASE_URL).origin : ''
}

/** 본문 → 준비 상태. 모양이 하나라도 어긋나면 `unreadable` */
export function readinessFromBody(httpStatus: number, text: string): Readiness {
  if (!READABLE_HTTP.has(httpStatus))
    return { kind: 'unreadable', reason: `HTTP ${String(httpStatus)}` }
  let body: unknown
  try {
    body = JSON.parse(text)
  } catch {
    return { kind: 'unreadable', reason: 'not json' }
  }
  if (typeof body !== 'object' || body === null || Array.isArray(body))
    return { kind: 'unreadable', reason: 'not json' }
  const { status, checks } = body as { status?: unknown; checks?: unknown }
  if (typeof status !== 'string' || !STATUSES.has(status))
    return { kind: 'unreadable', reason: 'no status' }
  if (typeof checks !== 'object' || checks === null || Array.isArray(checks))
    return { kind: 'unreadable', reason: 'no checks' }
  const entries = Object.entries(checks as Record<string, unknown>)
  // 🔴 빈 `checks` 는 「아무것도 안 봤다」다 — 「전부 정상」이 아니다(서버 `judge_readiness` 와 같은 자)
  if (entries.length === 0) return { kind: 'unreadable', reason: 'no checks' }
  const out: Record<string, string> = {}
  for (const [key, value] of entries) {
    if (typeof value !== 'string') return { kind: 'unreadable', reason: `checks.${key}` }
    out[key] = value
  }
  return { kind: 'read', status: status as HealthStatus['status'], checks: out, httpStatus }
}

/** `GET /readyz` 1회. **던지지 않는다** — 전송 오류도 `unreadable` 로 돌려준다 */
export async function fetchReadiness(): Promise<Readiness> {
  let response: Response
  try {
    response = await platform.http.fetch(`${rootUrl()}/readyz`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS.query),
    })
  } catch (cause) {
    return {
      kind: 'unreadable',
      reason: cause instanceof Error && cause.name !== '' ? cause.name : 'network',
    }
  }
  let text: string
  try {
    text = await response.text()
  } catch {
    return { kind: 'unreadable', reason: `HTTP ${String(response.status)}` }
  }
  return readinessFromBody(response.status, text)
}
